import type { GameSnapshot } from "../../game/runtime/types";
import type { OrbitForgeRuntime } from "../../game/runtime/runtime";
import { clamp, formatCompact } from "../../utils/math";
import { createGameIcon } from "../icon";

export function createProfilePanel(runtime: OrbitForgeRuntime): HTMLElement {
  const panel = document.createElement("section");
  panel.className = "of-panel of-profile-panel";
  panel.setAttribute("aria-label", "Profile");

  const header = document.createElement("div");
  header.className = "of-profile-panel-header";

  const title = document.createElement("div");
  title.className = "of-profile-panel-title";
  header.append(createGameIcon("profilePlayer", "Profile"), title);

  const stats = document.createElement("div");
  stats.className = "of-profile-stats";

  const xpProgress = document.createElement("div");
  xpProgress.className = "of-profile-xp";

  panel.append(header, xpProgress, stats);

  let wasOpen = false;
  let lastViewKey = "";

  runtime.subscribe((snapshot) => {
    const isOpen = snapshot.activePanel === "profile";
    if (isOpen !== wasOpen) {
      panel.classList.toggle("is-open", isOpen);
      wasOpen = isOpen;
    }

    if (!isOpen) {
      return;
    }

    const viewKey = profileViewKey(snapshot);
    if (viewKey === lastViewKey) {
      return;
    }

    lastViewKey = viewKey;
    renderProfileTitle(title, snapshot);
    renderXpProgress(xpProgress, snapshot);
    renderProfileStats(stats, snapshot);
  });

  return panel;
}

function profileViewKey(snapshot: GameSnapshot): string {
  return [
    snapshot.progress.level,
    snapshot.progress.prestigeLevel,
    snapshot.progress.title,
    Math.round(snapshot.progress.levelProgressPercent),
    formatCompact(snapshot.progress.xpIntoLevel),
    formatCompact(snapshot.progress.xpForNextLevel),
    snapshot.save.stats.BestPolarityStack,
    snapshot.effects.polarityWindowCount
  ].join("|");
}

function renderProfileTitle(title: HTMLElement, snapshot: GameSnapshot): void {
  const level = snapshot.progress.prestigeLevel > 0 ? `P${snapshot.progress.prestigeLevel}` : `LVL ${snapshot.progress.level}`;
  title.replaceChildren(textSpan(level, "of-profile-level"), textSpan(snapshot.progress.title, "of-profile-title"));
}

function renderProfileStats(stats: HTMLElement, snapshot: GameSnapshot): void {
  stats.replaceChildren(
    profileStat("BEST STACK", snapshot.save.stats.BestPolarityStack.toString()),
    profileStat("WINDOWS", snapshot.effects.polarityWindowCount.toString())
  );
}

function renderXpProgress(container: HTMLElement, snapshot: GameSnapshot): void {
  const percent = clamp(snapshot.progress.levelProgressPercent, 0, 100);
  const label = snapshot.progress.prestigeLevel > 0 ? "PRESTIGE XP" : "LEVEL XP";
  const nextLevel = snapshot.progress.prestigeLevel > 0 ? `P${Math.min(40, snapshot.progress.prestigeLevel + 1)}` : `LVL ${Math.min(40, snapshot.progress.level + 1)}`;

  const header = document.createElement("div");
  header.className = "of-profile-xp-header";
  header.append(textSpan(label, "of-profile-xp-label"), textSpan(`${Math.round(percent)}% TO ${nextLevel}`, "of-profile-xp-percent"));

  const track = document.createElement("div");
  track.className = "of-profile-xp-track";
  track.setAttribute("role", "progressbar");
  track.setAttribute("aria-label", label);
  track.setAttribute("aria-valuemin", "0");
  track.setAttribute("aria-valuemax", "100");
  track.setAttribute("aria-valuenow", Math.round(percent).toString());

  const fill = document.createElement("span");
  fill.className = "of-profile-xp-fill";
  fill.style.width = `${percent}%`;
  track.append(fill);

  const values = document.createElement("div");
  values.className = "of-profile-xp-values";
  values.append(
    textSpan(`${formatCompact(snapshot.progress.xpIntoLevel)} XP`, "of-profile-xp-value"),
    textSpan(`${formatCompact(snapshot.progress.xpForNextLevel)} NEXT`, "of-profile-xp-value")
  );

  container.replaceChildren(header, track, values);
}

function profileStat(label: string, value: string): HTMLElement {
  const item = document.createElement("span");
  item.className = "of-profile-stat";
  item.append(textSpan(label, "of-profile-stat-label"), textSpan(value, "of-profile-stat-value"));
  return item;
}

function textSpan(text: string, className: string): HTMLElement {
  const span = document.createElement("span");
  span.className = className;
  span.textContent = text;
  return span;
}
